import { Component, OnInit, HostListener } from '@angular/core';
import { NgClass } from '@angular/common';
import { LandingService } from '../../services/Landing_Services/landing.service';

@Component({
  selector: 'app-portfolio',
  standalone: true,
  imports: [NgClass],
  templateUrl: './portfolio.component.html',
  styleUrl: './portfolio.component.css'
})
export class PortfolioComponent implements OnInit {
  /** All top-level (parent) categories, each with a .children array */
  parentCategories: any[] = [];

  readonly INITIAL_VISIBLE = 6;
  showAll = false;

  /** The parent category currently open in the modal (null = modal closed) */
  activeModal: any | null = null;

  constructor(private landingService: LandingService) {}

  ngOnInit() {
    this.landingService.getProductCategories().subscribe({
      next: (categories) => {
        this.parentCategories = this.buildTree(categories);
        setTimeout(() => this.initScrollAnimations(), 0);
      },
      error: (error) => {
        console.error('Error fetching product categories:', error);
      }
    });
  }

  /**
   * Converts a flat list of categories (with parentId) into a tree.
   * Returns only top-level categories, each with a populated .children array.
   */
  private buildTree(flat: any[]): any[] {
    if (!Array.isArray(flat)) return [];

    // Map every category by id for fast child lookup
    const map = new Map<number, any>();
    flat.forEach(cat => map.set(cat.id, { ...cat, children: [] }));

    const roots: any[] = [];
    map.forEach(cat => {
      if (cat.parentId) {
        const parent = map.get(cat.parentId);
        if (parent) {
          parent.children.push(cat);
        }
        // if parent not found (orphan), ignore
      } else {
        roots.push(cat);
      }
    });

    return roots;
  }

  get visibleCategories(): any[] {
    return this.showAll
      ? this.parentCategories
      : this.parentCategories.slice(0, this.INITIAL_VISIBLE);
  }

  get hasMore(): boolean {
    return this.parentCategories.length > this.INITIAL_VISIBLE;
  }

  hasChildren(category: any): boolean {
    return Array.isArray(category.children) && category.children.length > 0;
  }

  openModal(category: any): void {
    this.activeModal = category;
    document.body.style.overflow = 'hidden';
  }

  closeModal(): void {
    this.activeModal = null;
    document.body.style.overflow = '';
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.closeModal();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.activeModal) this.closeModal();
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
    // Re-run animations after the new cards are painted
    setTimeout(() => this.initScrollAnimations(), 50);
  }

  private initScrollAnimations() {
    if (typeof document === 'undefined') return;
    const reveals = document.querySelectorAll('.portfolio-grid .reveal:not(.active)');
    if (!reveals.length) return;

    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    reveals.forEach((reveal) => revealObserver.observe(reveal));
  }
}
